import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import toastr from "react-redux-toastr";
import debounce from 'lodash/debounce';
import PayoutViewSettlementComponent from "./component";
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { format as formatDate, subDays } from 'date-fns';
import { toast } from "react-toastify";

const PayoutViewSettlementContainer = (props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settledPayouts, setSettledPayouts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // Partners state with pagination
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [hasMorePartners, setHasMorePartners] = useState(true);
  const [partnersPageNumber, setPartnersPageNumber] = useState(0);
  const [partnersPageSize] = useState(20);
  const [loadMorePartnersLoading, setLoadMorePartnersLoading] = useState(false);
  
  // Search states
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [isSearchingPartners, setIsSearchingPartners] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  
  const [franchiseeList, setFranchiseeList] = useState([]);
  const [filteredFranchiseeList, setFilteredFranchiseeList] = useState([]);
  const [pageNumber, setPageNumber] = useState(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Get today's date and calculate default dates (today - 2 days)
  const getDefaultDates = () => {
    const today = new Date();
    const twoDaysAgo = subDays(today, 1);
    
    return {
      startDate: formatDate(twoDaysAgo, 'yyyy-MM-dd'),
      endDate: formatDate(today, 'yyyy-MM-dd')
    };
  };

  const defaultDates = getDefaultDates();

  const [filters, setFilters] = useState({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    partnerId: '',
    limit: 20,
    offset: 0
  });

  // For filter popup - initially empty
  const [tempFilters, setTempFilters] = useState({
    startDate: null,
    endDate: null,
    partnerId: ''
  });

  // ✅ Fetch partners function with search capability
  const fetchPartners = useCallback(async (page = 0, isLoadMore = false, searchQuery = "") => {
    const isSearchingNow = !!searchQuery.trim();
    
    if ((isLoadMore && loadMorePartnersLoading) || (!isLoadMore && isSearchingPartners && !isSearchingNow)) return;
    
    if (isLoadMore) {
      setLoadMorePartnersLoading(true);
    } else if (isSearchingNow) {
      setIsSearchingPartners(true);
    }
    
    try {
      const params = new URLSearchParams({
        pageNumber: page,
        pageSize: partnersPageSize
      });

      // Add search parameter if provided
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await new Promise((resolve, reject) => {
        props.getDataFromAPI(
          `/dashboard/api/v2/admin/franchisee?${params.toString()}`,
          "GET",
          undefined,
          resolve,
          reject
        );
      });

      if (page === 0) {
        if (isSearchingNow) {
          setFilteredPartners(response || []);
          setSearchMode(true);
        } else {
          setPartners(response || []);
          setFilteredPartners(response || []);
          setSearchMode(false);
        }
      } else {
        if (isSearchingNow) {
          setFilteredPartners(prev => [...prev, ...(response || [])]);
        } else {
          const newPartners = [...partners, ...(response || [])];
          setPartners(newPartners);
          setFilteredPartners(newPartners);
        }
      }

      setHasMorePartners((response?.length || 0) === partnersPageSize);
      setPartnersPageNumber(page);
    } catch (err) {
      console.error("Partner API Error:", err);
      toast.error("Failed to load partners");
    } finally {
      if (isLoadMore) {
        setLoadMorePartnersLoading(false);
      } else if (isSearchingNow) {
        setIsSearchingPartners(false);
      }
    }
  }, [loadMorePartnersLoading, isSearchingPartners, props.getDataFromAPI, partnersPageSize, partners]);

  // ✅ Debounced search handler for dropdown
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim()) {
        // Search via API
        fetchPartners(0, false, query);
      } else {
        // Clear search - show all partners
        setSearchMode(false);
        if (partners.length === 0) {
          fetchPartners(0, false);
        } else {
          setFilteredPartners(partners);
        }
      }
    }, 300),
    [fetchPartners, partners]
  );

  // ✅ Handle dropdown search change
  const handleDropdownSearchChange = (e) => {
    const query = e.target.value;
    setDropdownSearch(query);
    debouncedSearch(query);
  };

  // ✅ Clear dropdown search
  const handleClearDropdownSearch = () => {
    setDropdownSearch("");
    setSearchMode(false);
    if (partners.length === 0) {
      fetchPartners(0, false);
    } else {
      setFilteredPartners(partners);
    }
  };

  // ✅ Load more button click handler for partners
  const handlePartnerLoadMore = () => {
    if (!loadMorePartnersLoading && hasMorePartners) {
      if (searchMode) {
        // Continue search pagination
        fetchPartners(partnersPageNumber + 1, true, dropdownSearch);
      } else {
        // Normal pagination
        fetchPartners(partnersPageNumber + 1, true);
      }
    }
  };

  // Initial load of partners
  useEffect(() => {
    fetchPartners(0, false);
  }, []);

  // Fetch settled payouts
  useEffect(() => {
    fetchSettledPayouts();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (!isInitialLoad) {
      fetchSettledPayouts();
    } else {
      setIsInitialLoad(false);
    }
  }, [filters.startDate, filters.endDate, filters.partnerId]);

  const fetchSettledPayouts = debounce((forExport = false, reset = false) => {
    if (!forExport && !reset && !hasMore) return;

    setLoading(true);

    const params = new URLSearchParams();
    params.append('startDate', filters.startDate || defaultDates.startDate);
    params.append('endDate', filters.endDate || defaultDates.endDate);
    if (filters.partnerId) params.append('franchiseeId', filters.partnerId);
    params.append('limit', forExport ? totalCount : filters.limit);
    params.append('offset', forExport ? 0 : filters.offset);

    console.log('Fetching with params:', params.toString());

    props.getDataFromAPI(
      `/partner/api/v3/acc/payouts/settled?${params.toString()}`,
      'GET',
      undefined,
      (response) => {
        if (forExport) {
          return response || [];
        } else {
          if (reset || filters.offset === 0) {
            setSettledPayouts(response || []);
          } else {
            setSettledPayouts(prev => [...prev, ...(response || [])]);
          }
          setTotalCount(response.totalCount || 0);
          setHasMore((response?.length || 0) >= filters.limit);
          setLoading(false);
        }
      },
      (err) => {
        console.error("API Error:", err);
        setError(err.message || "Failed to load settled payouts");
        toastr.error("Error", "Failed to load settled payouts");
        setLoading(false);
        return [];
      }
    );
  }, 300);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
      offset: 0
    }));

    setSettledPayouts([]);
    setHasMore(true);
  };

  // Handle temp filter changes
  const handleTempFilterChange = (name, value) => {
    setTempFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters from popup
  const handleApplyFilters = () => {
    const newFilters = {
      startDate: tempFilters.startDate ? formatDate(tempFilters.startDate, 'yyyy-MM-dd') : '',
      endDate: tempFilters.endDate ? formatDate(tempFilters.endDate, 'yyyy-MM-dd') : '',
      partnerId: tempFilters.partnerId
    };
    
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      offset: 0
    }));

    setSettledPayouts([]);
    setHasMore(true);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters(prev => ({
      ...prev,
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      partnerId: '',
      offset: 0
    }));
    
    setTempFilters({
      startDate: null,
      endDate: null,
      partnerId: ''
    });
    
    // Clear search
    setDropdownSearch("");
    setSearchMode(false);
    if (partners.length === 0) {
      fetchPartners(0, false);
    } else {
      setFilteredPartners(partners);
    }
    
    setSettledPayouts([]);
    setHasMore(true);
  };

  // Reset temp filters when popup opens
  const handleFilterPopupOpen = () => {
    setTempFilters({
      startDate: filters.startDate ? new Date(filters.startDate) : null,
      endDate: filters.endDate ? new Date(filters.endDate) : null,
      partnerId: filters.partnerId
    });
    
    // Reset search when popup opens
    setDropdownSearch("");
    setSearchMode(false);
    if (partners.length === 0) {
      fetchPartners(0, false);
    } else {
      setFilteredPartners(partners);
    }
  };

  // Manual load more for table data
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setFilters(prev => ({
        ...prev,
        offset: prev.offset + 1
      }));
      fetchSettledPayouts(false, false);
    }
  };

  const handleDownloadXLS = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append('startDate', filters.startDate || defaultDates.startDate);
      params.append('endDate', filters.endDate || defaultDates.endDate);
      if (filters.partnerId) params.append('partnerId', filters.partnerId);
      params.append('limit', 10000);
      params.append('offset', 0);

      const response = await new Promise((resolve, reject) => {
        props.getDataFromAPI(
          `/partner/api/v3/acc/payouts/settled?${params.toString()}`,
          'GET',
          undefined,
          (data) => resolve(data || []),
          (err) => reject(err)
        );
      });

      if (!response || response.length === 0) {
        toast.warning("No data available to download");
        return;
      }

      // Format with time
      const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';

        try {
          const date = new Date(dateString);
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        } catch (error) {
          return 'N/A';
        }
      };

      const excelData = [
        [
          'Start Date',
          'End Date',
          'Cohort ID',
          'Payout ID',
          'Partner ID',
          'Partner Name',
          'Type',
          'Revenue Today',
          'Total Deduction',
          'Net Revenue Today',
          'Payout'
        ],
        ...response.map(item => [
          formatDateTime(item.cohortSalesStart) || 'N/A',
          formatDateTime(item.cohortSalesEnd) || 'N/A',
          item.cohortId || 'N/A',
          item.payoutId || 'N/A',
          item.franchiseeId || 'N/A',
          item.name || 'N/A',
          item.type || 'N/A',
          `₹ ${item.revenueToday?.toLocaleString("en-IN") || '0'}`,
          `₹ ${item.totalDeductions?.toLocaleString("en-IN") || '0'}`,
          `₹ ${item.netPayable?.toLocaleString("en-IN") || '0'}`,
          `₹ ${item.generatedPayout?.toLocaleString("en-IN") || '0'}`
        ])
      ];

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Settled Payouts");
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      let fileName = `settled_payouts`;
      if (filters.startDate && filters.endDate) {
        fileName += `_${filters.startDate}_to_${filters.endDate}`;
      }
      if (filters.partnerId) {
        const partner = partners.find(p => p.id === filters.partnerId);
        fileName += `_${partner?.name || filters.partnerId}`;
      }
      fileName += '.xlsx';

      saveAs(blob, fileName);

    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download XLS file");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <PayoutViewSettlementComponent
      loading={loading}
      error={error}
      settledPayouts={settledPayouts}
      totalCount={totalCount}
      filters={filters}
      defaultDates={defaultDates}
      partners={filteredPartners}
      tempFilters={tempFilters}
      onFilterChange={handleFilterChange}
      onTempFilterChange={handleTempFilterChange}
      onApplyFilters={handleApplyFilters}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      handleDownloadXLS={handleDownloadXLS}
      handleClearFilters={handleClearFilters}
      onFilterPopupOpen={handleFilterPopupOpen}
      onGoBack={handleGoBack}
      onPartnerLoadMore={handlePartnerLoadMore}
      hasMorePartners={hasMorePartners}
      loadMorePartnersLoading={loadMorePartnersLoading}
      dropdownSearch={dropdownSearch}
      handleDropdownSearchChange={handleDropdownSearchChange}
      handleClearDropdownSearch={handleClearDropdownSearch}
      allPartners={partners}
      isSearchingPartners={isSearchingPartners}
      searchMode={searchMode}
    />
  );
};

export default connect(null, {
  getDataFromAPI,
})(PayoutViewSettlementContainer);